import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Atom, Microscope, Rocket, Search, Globe, ChevronRight } from 'lucide-react';

const Caltech: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "California Institute of Technology";

    return (
        <div className="min-h-screen bg-white font-sans text-[#333] selection:bg-[#FF6C0C] selection:text-white">
            {/* Top Bar */}
            <div className="bg-[#333] text-white py-1 px-4 flex justify-end text-[10px] font-bold uppercase tracking-widest gap-6">
                <a href="#" className="hover:text-[#FF6C0C]">Alumni</a>
                <a href="#" className="hover:text-[#FF6C0C]">Parents</a>
                <a href="#" className="hover:text-[#FF6C0C]">Visitors</a>
                <a href="#" className="hover:text-[#FF6C0C]">Media</a>
            </div>

            {/* Navbar */}
            <nav className="border-b border-gray-200 py-6 px-6 sticky top-0 bg-white/95 backdrop-blur z-50">
                <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between items-center">
                    <div className="flex items-center gap-2 mb-4 md:mb-0">
                        <div className="w-10 h-10 bg-[#FF6C0C] flex items-center justify-center text-white font-bold text-2xl tracking-tighter rounded-sm">
                            C
                        </div>
                        <span className="font-bold text-2xl tracking-tight text-[#333]">{schoolName.includes(' ') ? schoolName.split(' ')[0] : schoolName}</span>
                    </div>

                    <div className="flex items-center gap-8 font-bold text-sm text-[#333]">
                        <a href="#" className="hover:text-[#FF6C0C]">About</a>
                        <a href="#" className="hover:text-[#FF6C0C]">News</a>
                        <a href="#" className="hover:text-[#FF6C0C]">Research</a>
                        <a href="#" className="hover:text-[#FF6C0C]">Academics</a>
                        <a href="#" className="hover:text-[#FF6C0C]">Admissions</a>
                        <Search size={20} className="text-[#333] cursor-pointer hover:text-[#FF6C0C]" />
                    </div>
                </div>
            </nav>

            {/* Hero - Science / Space Focus */}
            <header className="relative bg-black h-[700px] overflow-hidden">
                <img 
                    src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop" 
                    alt="Space Technology" 
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent"></div>
                
                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8">
                     <div className="max-w-[1200px] mx-auto">
                         <h1 className="text-6xl md:text-8xl font-bold text-white mb-8 leading-none tracking-tighter">
                             Challenging <br/>
                             <span className="text-[#FF6C0C]">The Unknown.</span>
                         </h1>
                         <p className="text-xl md:text-2xl text-gray-300 max-w-2xl mb-12 font-light">
                             Investigating the most challenging, fundamental problems in science and technology in an interdisciplinary atmosphere.
                         </p>
                         <Button className="bg-[#FF6C0C] hover:bg-[#e05a00] text-white font-bold text-lg h-14 px-10 rounded-full">
                             Explore Our Impact
                         </Button>
                     </div>
                </div>
            </header>

            {/* Research Areas */}
            <section className="py-24 px-8 bg-[#f5f5f5]">
                <div className="max-w-[1200px] mx-auto">
                    <h2 className="text-3xl font-bold mb-12 flex items-center gap-2">
                        <Microscope className="text-[#FF6C0C]" /> Research Institutes
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { icon: Rocket, title: "JPL (Jet Propulsion Laboratory)", desc: "Managing NASA's center for robotic exploration of the solar system." },
                            { icon: Atom, title: "Quantum Information", desc: "Forging the future of computing and secure communication." },
                            { icon: Globe, title: "Sustainability", desc: "Resnick Sustainability Institute addresses global energy challenges." },
                        ].map((item, i) => (
                            <div key={i} className="bg-white p-8 shadow-sm hover:shadow-lg transition-shadow border-t-4 border-[#FF6C0C] group cursor-pointer">
                                <item.icon size={48} className="text-[#333] mb-6 group-hover:text-[#FF6C0C] transition-colors" />
                                <h3 className="text-xl font-bold mb-4">{item.title}</h3>
                                <p className="text-gray-600 leading-relaxed text-sm mb-6">{item.desc}</p>
                                <a href="#" className="text-[#FF6C0C] font-bold text-sm flex items-center gap-1 hover:gap-2 transition-all">
                                    Learn More <ChevronRight size={14} />
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* News - Minimal List */}
            <section className="py-24 px-8 bg-white">
                <div className="max-w-[1200px] mx-auto">
                    <div className="flex justify-between items-end mb-12">
                        <h2 className="text-3xl font-bold text-[#333]">Latest Discoveries</h2>
                        <a href="#" className="font-bold text-[#FF6C0C]">View All News</a>
                    </div>

                    <div className="space-y-8">
                        {[
                            { date: "Oct 12", title: "Astronomers find evidence of new planets in distant galaxy" },
                            { date: "Oct 10", title: "New bio-inspired material is stronger than steel" },
                            { date: "Oct 08", title: `${previewData?.name ? previewData.name.split(' ')[0] : 'Caltech'} researchers win Nobel Prize in Physics` }
                        ].map((news, i) => (
                            <div key={i} className="flex gap-8 items-center group cursor-pointer border-b border-gray-100 pb-8 last:border-0 last:pb-0">
                                <div className="text-[#FF6C0C] font-bold text-sm uppercase tracking-widest w-16 shrink-0">{news.date}</div>
                                <h3 className="text-2xl font-bold text-[#333] group-hover:text-[#FF6C0C] transition-colors">
                                    {news.title}
                                </h3>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#333] text-white py-16 px-8">
                <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between gap-12">
                    <div className="max-w-xs">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="w-8 h-8 bg-[#FF6C0C] flex items-center justify-center text-white font-bold rounded-sm">
                                C
                            </div>
                            <span className="font-bold text-xl">{schoolName.split(' ')[0]}</span>
                        </div>
                        <p className="text-sm text-gray-400">
                            {schoolName} <br/>
                            1200 E. California Blvd <br/>
                            Pasadena, CA 91125
                        </p>
                    </div>

                    <div className="flex gap-12 font-bold text-sm text-gray-300">
                        <ul className="space-y-4">
                            <li><a href="#" className="hover:text-[#FF6C0C]">Contact</a></li>
                            <li><a href="#" className="hover:text-[#FF6C0C]">Maps</a></li>
                            <li><a href="#" className="hover:text-[#FF6C0C]">Jobs</a></li>
                        </ul>
                        <ul className="space-y-4">
                            <li><a href="#" className="hover:text-[#FF6C0C]">Privacy</a></li>
                            <li><a href="#" className="hover:text-[#FF6C0C]">Accessibility</a></li>
                            <li><a href="#" className="hover:text-[#FF6C0C]">Title IX</a></li>
                        </ul>
                         <ul className="space-y-4">
                            <li><a href="#" className="hover:text-[#FF6C0C]">Facebook</a></li>
                            <li><a href="#" className="hover:text-[#FF6C0C]">Twitter</a></li>
                            <li><a href="#" className="hover:text-[#FF6C0C]">Instagram</a></li>
                        </ul>
                    </div>
                </div>
                 <div className="max-w-[1200px] mx-auto mt-12 pt-8 border-t border-gray-700 text-center text-xs text-gray-500">
                    © 2026 {schoolName}
                </div>
            </footer>
        </div>
    );
};

export default Caltech;
